import type { ClientAuthProvider } from './authProvider';
import type { SsoClientId } from './ssoClientConfig';
import { getLogger } from '../logger';

const log = getLogger('authState');

// Interface that encapsulates the current AuthStatus with its associated SSO token. We expect
// client teams to implement the ClientAuthProvider interface to provide this information.
export interface AuthState {
  authStatus: AuthStatus;
  ssoToken?: string;
}

export enum AuthStatus {
  AUTHENTICATED = 0,
  UNAUTHENTICATED = 1,
  UNKNOWN = 2,
}

/** @public */
export const unsetAuthState: AuthState = Object.freeze({
  authStatus: AuthStatus.UNKNOWN,
});

/** @public */
export const unauthState: AuthState = Object.freeze({
  authStatus: AuthStatus.UNAUTHENTICATED,
});

let ssoClientId: SsoClientId | null | undefined = undefined;
let authStatePromise: Promise<AuthState> | undefined = undefined;

/**
 * Initializes the authentication state based on the provided {@link clientAuthProvider}.
 *
 * 1. Observes authentication updates from the provider.
 * 2. Prior to any updates, the promise returned by {@link getAuthState} will be unresolved.
 * 3. When the first update is received, this promise is resolved.
 * 4. On future updates, this promise is replaced with an immediately resolved promise containing the
 *    new auth.
 *
 * @internal
 */
export function initializeAuthState(clientAuthProvider: ClientAuthProvider | null): void {
  // No client auth: always resolve unauthenticated
  if (clientAuthProvider === null) {
    log.debug('Setting no-op unauthenticated clientAuthProvider');
    authStatePromise = Promise.resolve(unauthState);
    ssoClientId = null;
    return;
  }

  ssoClientId = clientAuthProvider.ssoClientId;

  // Initially set authState to a promise which will resolve when we receive the first auth state from the clientAuthProvider.
  // Subsequently received auth states replace the original authState promise with an immediately resolved one.
  authStatePromise = new Promise<AuthState>(resolve => {
    clientAuthProvider.authState.subscribe(newAuthState => {
      log.debug('Got new authState:', newAuthState);

      if (newAuthState === unsetAuthState) {
        return;
      }

      resolve(newAuthState); // Resolve the original (this) promise
      authStatePromise = Promise.resolve(newAuthState); // Replace this promise with the new state.
    });
  });
}

/**
 * Returns the current authentication state, updated from the {@link ClientAuthProvider} provided to
 * {@link initializeAuthState}
 *
 * @internal
 * @see initializeAuthState
 */
// biome-ignore lint/suspicious/useAwait: Keeping api same as blizzard package.
export async function getAuthState(): Promise<AuthState> {
  if (!authStatePromise) {
    throw new Error('getAuthState called before setClientAuthProvider, was initBlizzard called?');
  }
  return authStatePromise;
}

/**
 * Returns the SSO client ID from the {@link ClientAuthProvider} provided to
 * {@link initializeAuthState}
 *
 * @internal
 * @see initializeAuthState
 */
export function getSsoClientId(): SsoClientId | null {
  if (ssoClientId === undefined) {
    throw new Error('getSsoClientId called before setClientAuthProvider, was initBlizzard called?');
  }
  return ssoClientId;
}
